import React, { useState } from "react";
import {
    StandardModal,
    StandardModalFormGroup,
    StandardModalHelperText,
    StandardModalLabel,
    StandardModalSection,
    StandardModalSectionHeader,
    StandardModalSectionTitle,
    StandardModalText,
    portalColors
} from "src/components";
import {
    BadgeRow
} from "src/components/CheckIn/CheckInControls.styled";
import CheckInCategorySelector from "src/components/CheckIn/CheckInCategorySelector";
import CheckInGoogleMap from "src/components/CheckIn/CheckInGoogleMap";
import CheckInLocationSearch from "src/components/CheckIn/CheckInLocationSearch";
import {
    CheckInBadge,
    CheckInLocationBadge
} from "src/components/CheckIn/CheckInBadges";
import {
    CHECK_IN_CATEGORY,
    checkInCountText,
    getCheckInCategory,
    hasValidCheckIn
} from "../checkIn";
import { shouldShowCheckInCategory } from "src/components/CheckIn/checkIn";

import {
    CheckInDisplayWrap,
    CountText,
    MapFrame, ModalContent,
} from "./CheckInSection.styled";

export const CheckInSection = ({ value, onChange, error }) => {
    const selectedCategory = value?.category || CHECK_IN_CATEGORY.LOCATION;

    const handleCategoryChange = (category) => {
        if (selectedCategory === category) {
            onChange({
                ...(value || {}),
                category: CHECK_IN_CATEGORY.LOCATION
            });
            return;
        }

        onChange({
            ...(value || {}),
            category
        });
    };

    const handleLocationSelected = (location) => {
        onChange({
            ...(value || {}),
            category: selectedCategory,
            ...location
        });
    };

    return (
        <StandardModalSection>
            <StandardModalSectionHeader>
                <div>
                    <StandardModalSectionTitle>Check In (Optional) </StandardModalSectionTitle>
                    <StandardModalHelperText margin="0.35rem 0 0">
                        Add a place to this post.
                    </StandardModalHelperText>
                </div>
            </StandardModalSectionHeader>
            <StandardModalFormGroup>
                <StandardModalLabel>Category</StandardModalLabel>
                <CheckInCategorySelector
                    value={value?.category}
                    onChange={handleCategoryChange}
                />
            </StandardModalFormGroup>
            <StandardModalFormGroup>
                <StandardModalLabel>Location</StandardModalLabel>
                <CheckInLocationSearch
                    value={value}
                    selectedCategory={selectedCategory}
                    onSelect={handleLocationSelected}
                    openOnCategoryChange
                    onClearSelectedPlace={emptyPlaceData => {
                        onChange({
                            ...(value || {}),
                            category: selectedCategory,
                            ...emptyPlaceData
                        });
                    }}
                />
                {error && <StandardModalHelperText color={portalColors.danger}>{error}</StandardModalHelperText>}
            </StandardModalFormGroup>
        </StandardModalSection>
    );
};

export const CheckInDisplay = ({ checkIn, detail, dashboard }) => {
    const [showMap, setShowMap] = useState(false);
    const count = checkInCountText(checkIn?.otherCheckInsCount);

    if (!hasValidCheckIn(checkIn)) {
        return null;
    }

    return (
        <>
            <CheckInDisplayWrap $detail={detail} $dashboard={dashboard}>
                <BadgeRow>
                    {shouldShowCheckInCategory(checkIn.category) && (
                        <CheckInBadge category={checkIn.category} />
                    )}
                    <CheckInLocationBadge
                        placeName={checkIn.placeName}
                        onClick={() => setShowMap(true)}
                    />
                </BadgeRow>
                {count && <CountText>{count}</CountText>}
            </CheckInDisplayWrap>
            <CheckInMapModal open={showMap} onClose={() => setShowMap(false)} checkIn={checkIn} />
        </>
    );
};

export const CheckInMapModal = ({ open, onClose, checkIn }) => {
    const category = getCheckInCategory(checkIn?.category);

    if (!checkIn) {
        return null;
    }

    const latitude = Number(checkIn.latitude);
    const longitude = Number(checkIn.longitude);

    const validCoordinates =
        Number.isFinite(latitude) &&
        Number.isFinite(longitude);

    return (
        <StandardModal
            open={open}
            onClose={onClose}
            title={checkIn.placeName}
            widthMode="large"
            bodyPadding="0"
        >
            <MapFrame>
                {validCoordinates ? (
                    <CheckInGoogleMap
                        latitude={latitude}
                        longitude={longitude}
                        placeId={checkIn.placeId}
                        placeName={checkIn.placeName}
                        formattedAddress={checkIn.formattedAddress}
                        placeImageReference={checkIn.placeImageReference}
                        category={category}
                    />
                ) : (
                    <StandardModalText color={portalColors.mutedText}>
                        Map location is unavailable.
                    </StandardModalText>
                )}
            </MapFrame>

            <ModalContent>
                <BadgeRow>
                    <CheckInBadge category={checkIn.category} />
                </BadgeRow>

                {checkIn.formattedAddress && (
                    <StandardModalText color={portalColors.text}>
                        {checkIn.formattedAddress}
                    </StandardModalText>
                )}

                {checkInCountText(checkIn.otherCheckInsCount) && (
                    <StandardModalText color={portalColors.mutedText}>
                        {checkInCountText(
                            checkIn.otherCheckInsCount
                        )}
                    </StandardModalText>
                )}
            </ModalContent>
        </StandardModal>
    );
};
